import { db } from '../config/firebase';
import {
  collection, getDocs, getDoc, doc, addDoc, setDoc, updateDoc,
  deleteDoc, query, where, serverTimestamp,
} from 'firebase/firestore';
import {
  IFR_FLOOR, MIN_GAMES_FOR_RATING, periodChange, initialRating,
  performanceRating, checkNorm, periodKeyOf, ageFrom, scoreFromResult,
} from './ifr';
import {
  PLAYER_TITLES, qualifiesFor, higherTitle,
  computeIFC, coachTitleFor, higherCoachTitle,
} from '../config/ifrTitles';
import { RATING_CATEGORIES, fieldsFor, ratingOf } from '../config/ratingCategories';

export const RC = {
  players: 'ratedPlayers',
  events: 'ratedEvents',
  games: 'ratedGames',
  periods: 'ratingPeriods',
  coaches: 'coaches',
};

// Rating inayotumika kwa mchezaji ambaye bado hana rating rasmi, ndani ya
// TUKIO LA MSINGI pekee (tukio la kwanza la kikundi kisichokuwa na rating).
export const BOOTSTRAP_RATING = 1200;

// ── Wachezaji ───────────────────────────────────────────────────────────
export async function fetchPlayers() {
  const snap = await getDocs(collection(db, RC.players));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function fetchPlayer(id) {
  const s = await getDoc(doc(db, RC.players, id));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

export async function savePlayer(data, id) {
  if (id) {
    await updateDoc(doc(db, RC.players, id), { ...data, updatedAt: serverTimestamp() });
    return id;
  }
  const ref = await addDoc(collection(db, RC.players), {
    ifr: null, established: false, gamesRated: 0, unratedGames: [],
    ifrRapid: null, establishedRapid: false, gamesRatedRapid: 0, unratedGamesRapid: [],
    ifrBlitz: null, establishedBlitz: false, gamesRatedBlitz: 0, unratedGamesBlitz: [],
    history: [], historyRapid: [], historyBlitz: [],
    norms: [], title: null, active: true,
    ...data,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deletePlayer(id) {
  await deleteDoc(doc(db, RC.players, id));
}

// ── Matukio ─────────────────────────────────────────────────────────────
export async function fetchEvents() {
  const snap = await getDocs(collection(db, RC.events));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
}

export async function fetchEvent(id) {
  const s = await getDoc(doc(db, RC.events, id));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

export async function saveEvent(data, id) {
  if (id) {
    await updateDoc(doc(db, RC.events, id), { ...data, updatedAt: serverTimestamp() });
    return id;
  }
  const ref = await addDoc(collection(db, RC.events), {
    status: 'draft',
    category: 'classical', // matukio ya zamani yote yalikuwa Classical
    ...data,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteEvent(id) {
  const games = await fetchGames({ eventId: id });
  for (const g of games) await deleteDoc(doc(db, RC.games, g.id));
  await deleteDoc(doc(db, RC.events, id));
}

// ── Michezo ─────────────────────────────────────────────────────────────
export async function fetchGames({ eventId, periodKey, playerId } = {}) {
  // Michezo ya mchezaji mmoja: anaweza kuwa mweupe au mweusi, hivyo
  // tunauliza server mara mbili na kuunganisha. Hii inatangulia ili
  // tusipakue collection nzima bure.
  if (playerId && !eventId && !periodKey) {
    const merged = {};
    for (const field of ['whiteId', 'blackId']) {
      try {
        const snap = await getDocs(query(collection(db, RC.games), where(field, '==', playerId)));
        snap.docs.forEach(d => { merged[d.id] = { id: d.id, ...d.data() }; });
      } catch (err) {
        console.warn('Kuuliza michezo ya mchezaji kumeshindikana:', err);
      }
    }
    return Object.values(merged);
  }

  const clauses = [];
  if (eventId) clauses.push(where('eventId', '==', eventId));
  if (periodKey) clauses.push(where('periodKey', '==', periodKey));

  let list;
  try {
    const qy = clauses.length
      ? query(collection(db, RC.games), ...clauses)
      : collection(db, RC.games);
    const snap = await getDocs(qy);
    list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('Kuchuja michezo kwa server kumeshindikana:', err);
    const snap = await getDocs(collection(db, RC.games));
    list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(g => (!eventId || g.eventId === eventId)
        && (!periodKey || g.periodKey === periodKey));
  }

  if (playerId) list = list.filter(g => g.whiteId === playerId || g.blackId === playerId);
  return list;
}

export async function addGame(data) {
  const ref = await addDoc(collection(db, RC.games), { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function deleteGame(id) {
  await deleteDoc(doc(db, RC.games, id));
}

// ── Vipindi ─────────────────────────────────────────────────────────────
export async function fetchPeriods() {
  const snap = await getDocs(collection(db, RC.periods));
  return snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.id.localeCompare(a.id));
}

// ══════════════════════════════════════════════════════════════════════
//  KUCHAPISHA KIPINDI — kwa KASI MOJA (classical | rapid | blitz)
//  Hesabu hii inafanya kazi mara tatu (moja kwa kila kasi) ndani ya
//  publishPeriod/recomputeAll. Titles na norms zinahesabiwa TU pale
//  category === 'classical' — Rapid na Blitz hazina title (kama FIDE).
// ══════════════════════════════════════════════════════════════════════
export function computeCategoryPeriod({ periodKey, category, players, events, games }) {
  const F = fieldsFor(category);
  const isClassical = category === 'classical';
  const byId = {};
  players.forEach(p => { byId[p.id] = { ...p }; });

  const periodEvents = events.filter(e => e.periodKey === periodKey && (e.category || 'classical') === category);
  const eventById = {};
  periodEvents.forEach(e => { eventById[e.id] = e; });
  const periodGames = games.filter(g => g.periodKey === periodKey && eventById[g.eventId]);

  function ratingForCalc(pid, eventIsBootstrap) {
    const p = byId[pid];
    if (!p) return null;
    if (p[F.established] && typeof p[F.ifr] === 'number') return p[F.ifr];
    return eventIsBootstrap ? BOOTSTRAP_RATING : null;
  }

  // 1) Kusanya michezo ya kila mchezaji
  const perPlayer = {};
  periodGames.forEach(g => {
    const ev = eventById[g.eventId];
    const boot = !!ev.bootstrap;
    [['white', g.whiteId, g.blackId], ['black', g.blackId, g.whiteId]].forEach(([color, me, opp]) => {
      if (!byId[me] || !byId[opp]) return;
      const oppRating = ratingForCalc(opp, boot);
      if (oppRating === null) return;
      const score = scoreFromResult(g.result, color);
      if (score === null) return;
      (perPlayer[me] = perPlayer[me] || []).push({
        gameId: g.id, eventId: g.eventId, eventName: ev.name, bootstrap: boot,
        opponentId: opp, opponentName: byId[opp].fullName, opponentRating: oppRating,
        opponentClubId: byId[opp].clubId || '', score, color,
      });
    });
  });

  // 2) Hesabu rating mpya
  const results = [];
  Object.entries(perPlayer).forEach(([pid, gs]) => {
    const p = byId[pid];
    const age = ageFrom(p.dob);

    if (p[F.established] && typeof p[F.ifr] === 'number') {
      const r = periodChange({ rating: p[F.ifr], gamesRated: p[F.gamesRated] || 0, age, games: gs });
      results.push({
        playerId: pid, name: p.fullName, kind: 'update',
        before: p[F.ifr], after: r.newRating, change: r.totalChange,
        k: r.k, games: gs, detail: r.detail,
      });
    } else {
      const prior = (p[F.unratedGames] || []).map(x => ({ ...x }));
      const all = [...prior, ...gs.map(g => ({
        opponentRating: g.opponentRating, score: g.score, eventId: g.eventId, periodKey,
      }))];
      const init = initialRating(all);
      if (init.established) {
        results.push({
          playerId: pid, name: p.fullName, kind: 'initial',
          before: null, after: init.rating, change: null,
          games: gs, allGames: all, performance: performanceRating(all),
        });
      } else {
        results.push({
          playerId: pid, name: p.fullName, kind: 'pending',
          before: null, after: null, reason: init.reason, games: gs, allGames: all,
        });
      }
    }
  });

  results.forEach(r => {
    if (r.after !== null && r.after !== undefined) {
      byId[r.playerId][F.ifr] = r.after;
      byId[r.playerId][F.established] = true;
    }
    byId[r.playerId][F.gamesRated] = (byId[r.playerId][F.gamesRated] || 0) + (r.games?.length || 0);
  });

  // 3) Norms na Titles — CLASSICAL PEKEE
  const normAwards = [];
  const titleAwards = [];
  if (isClassical) {
    Object.entries(perPlayer).forEach(([pid, gs]) => {
      const p = byId[pid];
      const byEvent = {};
      gs.forEach(g => { (byEvent[g.eventId] = byEvent[g.eventId] || []).push(g); });

      Object.entries(byEvent).forEach(([eid, egs]) => {
        const ev = eventById[eid];
        if (ev.bootstrap) return;
        const existing = (p.norms || []).map(n => `${n.eventId}__${n.title}`);
        PLAYER_TITLES.filter(t => t.norms > 0).forEach(t => {
          if (existing.includes(`${eid}__${t.code}`)) return;
          const res = checkNorm({
            games: egs, targetRating: t.rating, normPerformance: t.normPerformance,
            playerClubId: p.clubId || '',
          });
          if (res.achieved) {
            normAwards.push({
              playerId: pid, playerName: p.fullName, title: t.code,
              eventId: eid, eventName: ev.name, periodKey,
              performance: res.performance, avgOpponent: res.avgOpponent, games: egs.length,
            });
          }
        });
      });
    });

    Object.keys(perPlayer).forEach(pid => {
      const p = byId[pid];
      const myNorms = [...(p.norms || []), ...normAwards.filter(n => n.playerId === pid)];
      const rating = p[F.established] ? p[F.ifr] : null;
      if (!rating) return;
      const candidate = PLAYER_TITLES.find(t => {
        if (rating < t.rating) return false;
        if (t.norms === 0) return true;
        const count = myNorms.filter(n => {
          const nt = PLAYER_TITLES.find(x => x.code === n.title);
          return nt && nt.rating >= t.rating;
        }).length;
        return count >= t.norms;
      });
      const next = higherTitle(p.title, candidate?.code || null);
      if (next && next !== p.title) {
        titleAwards.push({ playerId: pid, playerName: p.fullName, title: next, rating, periodKey });
      }
    });
  }

  return {
    periodKey, category, events: periodEvents, gamesCount: periodGames.length,
    results, normAwards, titleAwards, playersAfter: byId,
  };
}

// Hakiki kabla ya kuchapisha — kasi ZOTE TATU kwa kipindi kimoja
export function computePeriod({ periodKey, players, events, games }) {
  const byCategory = {};
  RATING_CATEGORIES.forEach(c => {
    byCategory[c.key] = computeCategoryPeriod({ periodKey, category: c.key, players, events, games });
  });
  return byCategory; // { classical: {...}, rapid: {...}, blitz: {...} }
}

// Kuandika matokeo ya kipindi kwenye Firestore — kasi zote tatu kwa
// kubofya kimoja, kwa sababu ni kipindi kimoja cha mwezi.
export async function publishPeriod({ periodKey, author }) {
  const [players, events, games] = await Promise.all([
    fetchPlayers(), fetchEvents(), fetchGames(),
  ]);
  const byId = {};
  players.forEach(p => { byId[p.id] = p; });

  const byCategory = {};
  const patches = {}; // playerId -> patch inayokusanywa kutoka kasi zote

  RATING_CATEGORIES.forEach(c => {
    const F = fieldsFor(c.key);
    const out = computeCategoryPeriod({ periodKey, category: c.key, players, events, games });
    byCategory[c.key] = out;

    out.results.forEach(r => {
      const p = byId[r.playerId];
      patches[r.playerId] = patches[r.playerId] || { updatedAt: serverTimestamp() };
      const patch = patches[r.playerId];

      patch[F.gamesRated] = (p[F.gamesRated] || 0) + (r.games?.length || 0);

      if (r.kind === 'update' || r.kind === 'initial') {
        patch[F.ifr] = r.after;
        patch[F.established] = true;
        patch[F.unratedGames] = [];
        patch[F.history] = [
          ...(p[F.history] || []),
          { periodKey, rating: r.after, change: r.kind === 'initial' ? null : r.change, games: r.games.length, kind: r.kind },
        ];
      } else {
        patch[F.unratedGames] = r.allGames.map(g => ({
          opponentRating: g.opponentRating, score: g.score, eventId: g.eventId, periodKey: g.periodKey || periodKey,
        }));
      }

      if (c.key === 'classical') patch.lastActivePeriod = periodKey;
      else patch[F.lastActivePeriod] = periodKey;
    });

    if (c.key === 'classical') {
      out.results.forEach(r => {
        const p = byId[r.playerId];
        const patch = patches[r.playerId];
        const norms = [
          ...(p.norms || []),
          ...out.normAwards.filter(n => n.playerId === r.playerId).map(n => ({
            title: n.title, eventId: n.eventId, eventName: n.eventName, periodKey: n.periodKey, performance: n.performance,
          })),
        ];
        patch.norms = norms;
        const award = out.titleAwards.find(t => t.playerId === r.playerId);
        if (award) { patch.title = award.title; patch.titleAwardedAt = periodKey; }
        else if (!p.title && (r.after || p.ifr)) { patch.title = 'MR'; patch.titleAwardedAt = periodKey; }
      });
    }
  });

  for (const [playerId, patch] of Object.entries(patches)) {
    await updateDoc(doc(db, RC.players, playerId), patch);
  }

  for (const g of games.filter(x => x.periodKey === periodKey)) {
    await updateDoc(doc(db, RC.games, g.id), { rated: true, ratedAt: serverTimestamp() });
  }
  const allEvents = [...byCategory.classical.events, ...byCategory.rapid.events, ...byCategory.blitz.events];
  for (const e of allEvents) {
    await updateDoc(doc(db, RC.events, e.id), { status: 'rated', ratedAt: serverTimestamp() });
  }

  const standings = {};
  RATING_CATEGORIES.forEach(c => {
    const F = fieldsFor(c.key);
    standings[c.key] = Object.values(byCategory[c.key].playersAfter)
      .filter(p => p[F.established])
      .sort((a, b) => (b[F.ifr] || 0) - (a[F.ifr] || 0))
      .slice(0, 100)
      .map(p => ({ id: p.id, name: p.fullName, ifr: p[F.ifr], title: c.key === 'classical' ? (p.title || null) : null, club: p.clubName || '' }));
  });

  const totalGames = byCategory.classical.gamesCount + byCategory.rapid.gamesCount + byCategory.blitz.gamesCount;
  const totalPlayers = byCategory.classical.results.length + byCategory.rapid.results.length + byCategory.blitz.results.length;

  await setDoc(doc(db, RC.periods, periodKey), {
    periodKey,
    publishedAt: serverTimestamp(),
    publishedBy: author?.uid || '',
    publishedByName: author?.name || '',
    eventsCount: allEvents.length,
    gamesCount: totalGames,
    playersUpdated: totalPlayers,
    newTitles: byCategory.classical.titleAwards,
    newNorms: byCategory.classical.normAwards,
    standings: standings.classical, // urithi wa toleo la awali (orodha bapa)
    standingsByCategory: standings,
    perCategory: {
      classical: { games: byCategory.classical.gamesCount, players: byCategory.classical.results.length },
      rapid: { games: byCategory.rapid.gamesCount, players: byCategory.rapid.results.length },
      blitz: { games: byCategory.blitz.gamesCount, players: byCategory.blitz.results.length },
    },
  });

  return byCategory;
}

// ══════════════════════════════════════════════════════════════════════
//  KUHESABU UPYA KILA KITU — kasi zote tatu
// ══════════════════════════════════════════════════════════════════════
export async function recomputeAll({ author, onProgress } = {}) {
  const [playersRaw, events, games] = await Promise.all([
    fetchPlayers(), fetchEvents(), fetchGames(),
  ]);

  let players = playersRaw.map(p => ({
    ...p,
    ifr: null, established: false, gamesRated: 0, unratedGames: [], history: [], lastActivePeriod: null,
    ifrRapid: null, establishedRapid: false, gamesRatedRapid: 0, unratedGamesRapid: [], historyRapid: [], lastActivePeriodRapid: null,
    ifrBlitz: null, establishedBlitz: false, gamesRatedBlitz: 0, unratedGamesBlitz: [], historyBlitz: [], lastActivePeriodBlitz: null,
    norms: [], title: null, titleAwardedAt: null,
  }));

  const periods = Array.from(new Set(events.map(e => e.periodKey).filter(Boolean))).sort();

  for (const pk of periods) {
    for (const c of RATING_CATEGORIES) {
      const F = fieldsFor(c.key);
      const out = computeCategoryPeriod({ periodKey: pk, category: c.key, players, events, games });
      const byId = {};
      players.forEach(p => { byId[p.id] = p; });

      out.results.forEach(r => {
        const p = byId[r.playerId];
        p[F.gamesRated] = (p[F.gamesRated] || 0) + (r.games?.length || 0);
        if (c.key === 'classical') p.lastActivePeriod = pk; else p[F.lastActivePeriod] = pk;

        if (r.kind === 'update' || r.kind === 'initial') {
          p[F.ifr] = r.after;
          p[F.established] = true;
          p[F.unratedGames] = [];
          p[F.history] = [...(p[F.history] || []), { periodKey: pk, rating: r.after, change: r.kind === 'initial' ? null : r.change, games: r.games.length, kind: r.kind }];
        } else {
          p[F.unratedGames] = r.allGames.map(g => ({ opponentRating: g.opponentRating, score: g.score, eventId: g.eventId, periodKey: g.periodKey || pk }));
        }

        if (c.key === 'classical') {
          p.norms = [
            ...(p.norms || []),
            ...out.normAwards.filter(n => n.playerId === r.playerId).map(n => ({
              title: n.title, eventId: n.eventId, eventName: n.eventName, periodKey: n.periodKey, performance: n.performance,
            })),
          ];
          const award = out.titleAwards.find(t => t.playerId === r.playerId);
          if (award) { p.title = award.title; p.titleAwardedAt = pk; }
          else if (!p.title && p.ifr) { p.title = 'MR'; p.titleAwardedAt = pk; }
        }
      });

      players = Object.values(byId);
    }
    if (onProgress) onProgress(`Kipindi ${pk} kimehesabiwa (kasi zote tatu)`);
  }

  for (const p of players) {
    await updateDoc(doc(db, RC.players, p.id), {
      ifr: p.ifr, established: p.established, gamesRated: p.gamesRated, unratedGames: p.unratedGames || [], history: p.history || [], lastActivePeriod: p.lastActivePeriod || null,
      ifrRapid: p.ifrRapid, establishedRapid: p.establishedRapid, gamesRatedRapid: p.gamesRatedRapid, unratedGamesRapid: p.unratedGamesRapid || [], historyRapid: p.historyRapid || [], lastActivePeriodRapid: p.lastActivePeriodRapid || null,
      ifrBlitz: p.ifrBlitz, establishedBlitz: p.establishedBlitz, gamesRatedBlitz: p.gamesRatedBlitz, unratedGamesBlitz: p.unratedGamesBlitz || [], historyBlitz: p.historyBlitz || [], lastActivePeriodBlitz: p.lastActivePeriodBlitz || null,
      norms: p.norms || [], title: p.title || null, titleAwardedAt: p.titleAwardedAt || null,
      recomputedAt: serverTimestamp(), recomputedBy: author?.name || '',
    });
  }

  return { periods, players: players.length };
}

// ══════════════════════════════════════════════════════════════════════
//  MAKADIRIO YA MOJA KWA MOJA — wakati tukio linaendelea
//  Haiandiki chochote Firestore. Inatumia rating RASMI ya SASA (ile
//  iliyochapishwa mwisho) kama msingi, kisha inahesabu vipi michezo
//  ya tukio la leo (bado halijachapishwa) ingebadilisha rating hiyo —
//  ili washiriki waone dalili ya mwenendo wao SIKU HIYO HIYO, bila
//  kuvunja kanuni ya "kipindi cha mwezi" inayozuia udanganyifu.
// ══════════════════════════════════════════════════════════════════════
export function liveProjection({ player, category, liveGames }) {
  const F = fieldsFor(category);
  const baseline = player[F.established] ? player[F.ifr] : BOOTSTRAP_RATING;
  const age = ageFrom(player.dob);
  const gs = liveGames.map(g => ({ opponentRating: g.opponentRating, score: g.score }));
  if (!gs.length) return { baseline, projected: baseline, change: 0, established: !!player[F.established] };

  if (player[F.established]) {
    const r = periodChange({ rating: baseline, gamesRated: player[F.gamesRated] || 0, age, games: gs });
    return { baseline, projected: r.newRating, change: r.totalChange, established: true };
  }
  const prior = (player[F.unratedGames] || []).map(x => ({ ...x }));
  const init = initialRating([...prior, ...gs]);
  return {
    baseline: null,
    projected: init.established ? init.rating : null,
    change: null,
    established: false,
    note: init.established ? null : init.reason,
  };
}

// ══════════════════════════════════════════════════════════════════════
//  IFC — MAKOCHA
// ══════════════════════════════════════════════════════════════════════
export async function fetchCoaches() {
  const snap = await getDocs(collection(db, RC.coaches));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.ifc || 0) - (a.ifc || 0));
}

export async function saveCoach(data, id) {
  if (id) {
    await updateDoc(doc(db, RC.coaches, id), { ...data, updatedAt: serverTimestamp() });
    return id;
  }
  const ref = await addDoc(collection(db, RC.coaches), { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function deleteCoach(id) {
  await deleteDoc(doc(db, RC.coaches, id));
}

export function computeCoachRecord(coach, { students, sessions, attendanceRate }) {
  const mine = students.filter(s => (coach.studentIds || []).includes(s.id) || s.coachId === coach.id);
  const rated = mine.filter(s => s.established);
  const titled = mine.filter(s => s.title && s.title !== 'MR');

  const gains = rated.map(s => {
    const h = (s.history || []).slice(-12);
    if (h.length < 2) return 0;
    return (h[h.length - 1].rating || 0) - (h[0].rating || 0);
  });
  const avgGain = gains.length ? Math.round(gains.reduce((a, b) => a + b, 0) / gains.length) : 0;

  const inputs = {
    studentsRated: rated.length,
    avgRatingGain: Math.max(0, avgGain),
    titledStudents: titled.length,
    sessions: sessions || 0,
    attendanceRate: attendanceRate || 0,
    certifications: (coach.certifications || []).length,
    yearsActive: Number(coach.yearsActive) || 0,
  };

  const { parts, ifc } = computeIFC(inputs);
  const candidate = coachTitleFor(ifc, inputs);
  const title = higherCoachTitle(coach.title, candidate?.code || null);

  return { inputs, parts, ifc, title, studentsRated: rated.length, titledStudents: titled.length };
}

export { periodKeyOf, IFR_FLOOR, MIN_GAMES_FOR_RATING, qualifiesFor, ratingOf };
